import React, { useState } from "react";
import logo from "../assets/logo_nobg.png";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

const STAGES = [
  "Just an Idea",
  "Prototype / MVP",
  "Early Revenue",
  "Scaling",
  "Seeking Strategic Capital",
];

const FUNDING_RANGES = [
  "< ₹10L",
  "₹10L - ₹50L",
  "₹50L - ₹1Cr",
  "₹1Cr - ₹5Cr",
  "₹5Cr+",
  "Open to Discussion",
];

const STEPS = [
  { label: "About You",  num: 1 },
  { label: "Your Idea",  num: 2 },
  { label: "The Ask",    num: 3 },
];

const initialForm = {
  from_name: "", from_email: "", phone: "", linkedin: "",
  startup_name: "", one_liner: "", problem: "",
  stage: "", funding: "", why_back: "",
};

export default function VenturesForm() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [touched, setTouched] = useState({});

  const validate = (data, currentStep) => {
    const e = {};
    if (currentStep === 0 || currentStep === undefined) {
      if (!data.from_name.trim()) e.from_name = "Required";
      if (!data.from_email.trim()) e.from_email = "Required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.from_email)) e.from_email = "Enter a valid email";
    }
    if (currentStep === 1 || currentStep === undefined) {
      if (!data.startup_name.trim()) e.startup_name = "Required";
      if (!data.one_liner.trim()) e.one_liner = "Required";
      if (!data.stage) e.stage = "Select a stage";
    }
    if (currentStep === 2 || currentStep === undefined) {
      if (!data.funding) e.funding = "Select a range";
      if (!data.why_back.trim()) e.why_back = "Required";
    }
    return e;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (touched[field]) {
      const e = validate({ ...form, [field]: value }, step);
      setErrors((prev) => ({ ...prev, [field]: e[field] }));
    }
  };

  const handleBlur = (field, overrideValue) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const data = overrideValue !== undefined ? { ...form, [field]: overrideValue } : form;
    const e = validate(data, step);
    setErrors((prev) => ({ ...prev, [field]: e[field] }));
  };

  const nextStep = () => {
    const fields = step === 0 ? ["from_name", "from_email"] : step === 1 ? ["startup_name", "one_liner", "stage"] : ["funding", "why_back"];
    const allTouched = {};
    fields.forEach((f) => (allTouched[f] = true));
    setTouched((prev) => ({ ...prev, ...allTouched }));
    const e = validate(form, step);
    const stepErrors = {};
    fields.forEach((f) => { if (e[f]) stepErrors[f] = e[f]; });
    if (Object.keys(stepErrors).length > 0) { setErrors((prev) => ({ ...prev, ...stepErrors })); return; }
    setStep((s) => Math.min(s + 1, 2));
  };

  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    const allTouched = {};
    Object.keys(form).forEach((f) => (allTouched[f] = true));
    setTouched(allTouched);
    const e = validate(form, undefined);
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setStatus("sending");
    try {
      await addDoc(collection(db, "venture_submissions"), { ...form, submittedAt: serverTimestamp() });
      setStatus("success");
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  if (status === "success") return <SuccessScreen />;

  return (
    <div className="vf">
      <style>{CSS}</style>

      <nav className="vf-nav">
        <div className="vf-nav-inner">
          <a href="https://shashankupadhyaya.com" className="vf-logo">
            <img src={logo} alt="S/U" className="site-logo-img" />
            <span className="vf-logo-label">Ventures</span>
          </a>
          <a href="https://shashankupadhyaya.com" className="vf-nav-back">← Back to portfolio</a>
        </div>
      </nav>

      <div className="vf-page">
        <div className="vf-layout">

          {/* Left panel */}
          <div className="vf-panel">
            <span className="vf-chip">Ventures</span>
            <h1 className="vf-h1">Let's build something extraordinary.</h1>
            <p className="vf-desc">I personally back founders with bold ideas and real conviction. Share your vision — I read every submission.</p>
            <div className="vf-sidebar-steps">
              {STEPS.map((s, i) => (
                <div key={i} className={`vf-sidebar-step${i === step ? " active" : i < step ? " done" : ""}`}>
                  <div className="vf-sidebar-step-num">{i < step ? "✓" : s.num}</div>
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Form panel */}
          <div className="vf-form-panel">

            {/* Progress bar */}
            <div className="vf-progress">
              <div className="vf-progress-bar" style={{ width: `${((step + 1) / 3) * 100}%` }} />
            </div>

            <div className="vf-form-body">
              <div className="vf-step-header">
                <span className="vf-step-count">Step {step + 1} of 3</span>
                <h2 className="vf-step-title">{STEPS[step].label}</h2>
              </div>

              <div key={step} className="vf-fields">
                {step === 0 && <Step1 form={form} errors={errors} onChange={handleChange} onBlur={handleBlur} />}
                {step === 1 && <Step2 form={form} errors={errors} onChange={handleChange} onBlur={handleBlur} />}
                {step === 2 && <Step3 form={form} errors={errors} onChange={handleChange} onBlur={handleBlur} />}
              </div>

              {status === "error" && (
                <div className="vf-err-banner">Something went wrong. Please try again.</div>
              )}

              <div className="vf-form-actions">
                {step > 0 && (
                  <button className="vf-btn vf-btn--ghost" onClick={prevStep}>← Back</button>
                )}
                <div style={{ flex: 1 }} />
                {step < 2
                  ? <button className="vf-btn vf-btn--gold" onClick={nextStep}>Continue →</button>
                  : <button className="vf-btn vf-btn--gold" onClick={handleSubmit} disabled={status === "sending"}>
                      {status === "sending" ? "Submitting…" : "Submit Application →"}
                    </button>
                }
              </div>

              <p className="vf-disclaimer">
                Your information is used solely to evaluate investment fit and kept confidential.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Step1({ form, errors, onChange, onBlur }) {
  return (
    <>
      <div className="vf-grid-2">
        <Field label="Full Name" required error={errors.from_name}>
          <input className={`vf-input${errors.from_name ? " vf-input--err" : ""}`}
            placeholder="Your full name"
            value={form.from_name} onChange={(e) => onChange("from_name", e.target.value)}
            onBlur={() => onBlur("from_name")} />
        </Field>
        <Field label="Email" required error={errors.from_email}>
          <input className={`vf-input${errors.from_email ? " vf-input--err" : ""}`}
            placeholder="your@email.com" type="email"
            value={form.from_email} onChange={(e) => onChange("from_email", e.target.value)}
            onBlur={() => onBlur("from_email")} />
        </Field>
      </div>
      <div className="vf-grid-2">
        <Field label="Phone">
          <input className="vf-input" placeholder="+91 98765 43210"
            value={form.phone} onChange={(e) => onChange("phone", e.target.value)} />
        </Field>
        <Field label="LinkedIn / Website">
          <input className="vf-input" placeholder="linkedin.com/in/..."
            value={form.linkedin} onChange={(e) => onChange("linkedin", e.target.value)} />
        </Field>
      </div>
    </>
  );
}

function Step2({ form, errors, onChange, onBlur }) {
  return (
    <>
      <div className="vf-grid-2">
        <Field label="Startup / Idea Name" required error={errors.startup_name}>
          <input className={`vf-input${errors.startup_name ? " vf-input--err" : ""}`}
            placeholder="Your startup name"
            value={form.startup_name} onChange={(e) => onChange("startup_name", e.target.value)}
            onBlur={() => onBlur("startup_name")} />
        </Field>
        <Field label="One-line Pitch" required error={errors.one_liner}>
          <input className={`vf-input${errors.one_liner ? " vf-input--err" : ""}`}
            placeholder="Describe your idea in one sentence"
            value={form.one_liner} onChange={(e) => onChange("one_liner", e.target.value)}
            onBlur={() => onBlur("one_liner")} />
        </Field>
      </div>
      <Field label="Problem You're Solving">
        <textarea className="vf-input vf-textarea" rows={3}
          placeholder="Describe the problem and who faces it"
          value={form.problem} onChange={(e) => onChange("problem", e.target.value)} />
      </Field>
      <Field label="Current Stage" required error={errors.stage}>
        <div className="vf-pills">
          {STAGES.map((s) => (
            <button key={s} type="button"
              className={`vf-pill${form.stage === s ? " vf-pill--sel" : ""}`}
              onClick={() => { onChange("stage", s); onBlur("stage", s); }}>
              {s}
            </button>
          ))}
        </div>
      </Field>
    </>
  );
}

function Step3({ form, errors, onChange, onBlur }) {
  return (
    <>
      <Field label="Funding Range" required error={errors.funding}>
        <div className="vf-pills">
          {FUNDING_RANGES.map((r) => (
            <button key={r} type="button"
              className={`vf-pill${form.funding === r ? " vf-pill--sel" : ""}`}
              onClick={() => { onChange("funding", r); onBlur("funding", r); }}>
              {r}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Why should I back you?" required error={errors.why_back}>
        <textarea className={`vf-input vf-textarea${errors.why_back ? " vf-input--err" : ""}`}
          rows={6} placeholder="Your unfair advantage, traction, team, vision — make your case"
          value={form.why_back} onChange={(e) => onChange("why_back", e.target.value)}
          onBlur={() => onBlur("why_back")} />
      </Field>
    </>
  );
}

function SuccessScreen() {
  return (
    <div className="vf vf--center">
      <style>{CSS}</style>
      <div className="vf-success">
        <div className="vf-success-icon">✓</div>
        <h2 className="vf-success-title">Application Submitted!</h2>
        <p className="vf-success-sub">Thank you for reaching out. I'll get back to you if there's a fit. Good luck!</p>
        <a href="https://shashankupadhyaya.com" className="vf-btn vf-btn--gold">← Back to Portfolio</a>
      </div>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div className="vf-field">
      <label className="vf-label">
        {label}{required && <span className="vf-required"> *</span>}
      </label>
      {children}
      {error && <span className="vf-field-err">{error}</span>}
    </div>
  );
}

const CSS = `
  .vf {
    --bg: #0A0B0D;
    --card: #0E1116;
    --border: rgba(255,255,255,0.08);
    --text: #EDEFF2;
    --muted: #9AA3AF;
    --gold: #C7F94B;
    --gold-dim: rgba(199,249,75,0.12);
    --err: #ef4444;
    --nav-h: 64px;
    --radius: 8px;
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    font-size: 15px;
    -webkit-font-smoothing: antialiased;
  }
  .vf *, .vf *::before, .vf *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .vf a { color: inherit; text-decoration: none; }
  .vf button { font-family: inherit; cursor: pointer; }
  .vf--center { display: flex; align-items: center; justify-content: center; }

  /* Nav */
  .vf-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 50;
    height: var(--nav-h); background: rgba(10,11,13,0.95);
    backdrop-filter: blur(20px); border-bottom: 1px solid var(--border);
  }
  .vf-nav-inner {
    max-width: 1200px; margin: 0 auto; padding: 0 32px;
    height: 100%; display: flex; align-items: center;
  }
  .vf-logo { display: flex; align-items: center; gap: 10px; }
  .site-logo-img { height: 56px; width: auto; display: block; object-fit: contain; }
  .vf-logo-label { font-size: 13px; color: var(--muted); padding-left: 10px; border-left: 1px solid var(--border); }
  .vf-nav-back { font-size: 13px; color: var(--muted); margin-left: auto; transition: color 0.15s; }
  .vf-nav-back:hover { color: var(--gold); }

  /* Page */
  .vf-page { padding-top: var(--nav-h); min-height: 100vh; display: flex; align-items: stretch; }
  .vf-layout { display: grid; grid-template-columns: 380px 1fr; width: 100%; max-width: 1200px; margin: 0 auto; }

  /* Left panel */
  .vf-panel {
    background: var(--card); border-right: 1px solid var(--border);
    padding: 60px 40px; display: flex; flex-direction: column; gap: 24px;
  }
  .vf-chip {
    display: inline-block; font-size: 11px; font-weight: 600; letter-spacing: 0.08em;
    text-transform: uppercase; color: var(--gold);
    background: var(--gold-dim); border: 1px solid rgba(199,249,75,0.25);
    padding: 4px 10px; border-radius: 4px;
  }
  .vf-h1 {
    font-family: 'Archivo', sans-serif; font-weight: 900;
    font-size: clamp(24px, 2.5vw, 32px); letter-spacing: -0.025em;
    line-height: 1.15;
  }
  .vf-desc { font-size: 14px; color: var(--muted); line-height: 1.75; }
  .vf-sidebar-steps { display: flex; flex-direction: column; gap: 8px; margin-top: 16px; }
  .vf-sidebar-step {
    display: flex; align-items: center; gap: 12px;
    padding: 12px 16px; border-radius: var(--radius); border: 1px solid transparent;
    font-size: 14px; color: var(--muted); transition: all 0.2s;
  }
  .vf-sidebar-step.active { border-color: rgba(199,249,75,0.3); background: var(--gold-dim); color: var(--text); }
  .vf-sidebar-step.done { color: var(--muted); }
  .vf-sidebar-step-num {
    width: 28px; height: 28px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 700; flex-shrink: 0;
    background: var(--border); color: var(--muted); transition: all 0.2s;
  }
  .vf-sidebar-step.active .vf-sidebar-step-num { background: var(--gold); color: #000; }
  .vf-sidebar-step.done .vf-sidebar-step-num { background: rgba(199,249,75,0.3); color: var(--gold); }

  /* Form panel */
  .vf-form-panel { display: flex; flex-direction: column; }
  .vf-progress { height: 3px; background: var(--border); }
  .vf-progress-bar { height: 100%; background: var(--gold); transition: width 0.4s ease; }
  .vf-form-body { flex: 1; padding: 48px 48px 40px; max-width: 640px; }
  .vf-step-header { margin-bottom: 32px; }
  .vf-step-count { font-size: 12px; color: var(--muted); letter-spacing: 0.06em; text-transform: uppercase; display: block; margin-bottom: 8px; }
  .vf-step-title { font-family: 'Archivo', sans-serif; font-weight: 700; font-size: 22px; letter-spacing: -0.02em; }

  /* Fields */
  .vf-fields { display: flex; flex-direction: column; gap: 20px; margin-bottom: 32px; }
  .vf-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .vf-field { display: flex; flex-direction: column; gap: 6px; }
  .vf-label { font-size: 13px; font-weight: 600; color: var(--text); }
  .vf-required { color: var(--gold); }
  .vf-input {
    background: rgba(255,255,255,0.04); border: 1px solid var(--border);
    border-radius: var(--radius); color: var(--text); outline: none;
    padding: 11px 14px; font-size: 14px; font-family: inherit;
    transition: border-color 0.15s, background 0.15s; width: 100%;
  }
  .vf-input::placeholder { color: var(--muted); }
  .vf-input:focus { border-color: var(--gold); background: rgba(199,249,75,0.04); }
  .vf-input--err { border-color: var(--err); }
  .vf-textarea { resize: vertical; min-height: 80px; }
  .vf-field-err { font-size: 12px; color: var(--err); }

  /* Pills */
  .vf-pills { display: flex; flex-wrap: wrap; gap: 8px; }
  .vf-pill {
    font-size: 13px; padding: 8px 16px; border-radius: 6px;
    border: 1px solid var(--border); background: transparent; color: var(--muted);
    transition: all 0.15s;
  }
  .vf-pill:hover { border-color: rgba(199,249,75,0.4); color: var(--text); }
  .vf-pill--sel { border-color: var(--gold); color: var(--gold); background: var(--gold-dim); }

  /* Actions */
  .vf-form-actions { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
  .vf-btn {
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 14px; font-weight: 600; padding: 11px 24px;
    border-radius: var(--radius); border: none; cursor: pointer;
    transition: all 0.15s;
  }
  .vf-btn--gold { background: var(--gold); color: #000; }
  .vf-btn--gold:hover:not(:disabled) { opacity: 0.88; }
  .vf-btn--gold:disabled { opacity: 0.5; cursor: not-allowed; }
  .vf-btn--ghost { background: transparent; color: var(--muted); border: 1px solid var(--border); }
  .vf-btn--ghost:hover { color: var(--text); border-color: rgba(255,255,255,0.2); }

  .vf-err-banner {
    background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3);
    border-radius: var(--radius); padding: 12px 16px;
    font-size: 13px; color: #ef4444; margin-bottom: 16px;
  }
  .vf-disclaimer { font-size: 12px; color: var(--muted); line-height: 1.6; }

  /* Success */
  .vf-success {
    text-align: center; padding: 60px 32px;
    display: flex; flex-direction: column; align-items: center; gap: 16px;
    max-width: 400px;
  }
  .vf-success-icon {
    width: 64px; height: 64px; border-radius: 50%;
    background: var(--gold-dim); border: 2px solid var(--gold);
    display: flex; align-items: center; justify-content: center;
    font-size: 28px; color: var(--gold);
  }
  .vf-success-title { font-family: 'Archivo', sans-serif; font-weight: 800; font-size: 26px; letter-spacing: -0.02em; }
  .vf-success-sub { font-size: 15px; color: var(--muted); line-height: 1.7; }

  /* Responsive */
  @media (max-width: 900px) {
    .vf-layout { grid-template-columns: 1fr; }
    .vf-panel { padding: 40px 28px; border-right: none; border-bottom: 1px solid var(--border); }
    .vf-sidebar-steps { flex-direction: row; flex-wrap: wrap; }
    .vf-form-body { padding: 32px 28px; }
  }
  @media (max-width: 560px) {
    .vf-grid-2 { grid-template-columns: 1fr; }
    .vf-form-body { padding: 24px 20px; }
    .vf-sidebar-steps { flex-direction: column; }
    .vf-panel { padding: 28px 20px; }
  }
  @media (max-width: 480px) {
    .site-logo-img { height: 40px; }
    .vf-logo-label { display: none; }
    .vf-nav-back { display: none; }
    .vf-nav-inner { padding: 0 16px; }
    .vf-btn { padding: 10px 14px; font-size: 13px; }
    .vf-form-body { padding: 20px 16px; }
  }
`;
